import React, { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Button, Icon, palette } from '@centivo/react-components'
import { useToggle } from 'src/hooks'
import { isArray } from 'lodash'

import { Spinner } from 'src/refactor/components'
import { usePractitionerDetails } from 'src/refactor/hooks'
import { TPractitionerIdentifiers } from 'src/refactor/hooks/use-practitioner-details/usePractitionerDetails'
import { ContentSection } from 'src/refactor/structure'
import { Map, Modal } from 'src/refactor/blocks'
import { useMediaContext } from 'src/context/responsive'
import { formatNameWithTitle } from 'src/context/results/helpers'
import centivoLogo from 'src/assets/Images/CENHEA_18623-1_IconWithTrademark_4C-01@2x.png'

import { useSliceTemplate } from 'src/context/global'
import { PractitionerDetailsSlice } from './PractitionerDetailsSlice'

import {
  aboutData,
  checkHasPracticeContact,
  checkHasPracticeFax,
  checkHasPracticePhone,
  checkIsVirtualCare,
  checkIsVirtualCareOnly,
  getModalStyle,
  sanitizeUrl,
} from './functions/practitionerDetailsFunctions'

import {
  Column,
  RedirectModal,
  StyledPractitionerDetails,
} from './PractitionerDetails.styled'

const PractitionerAttribute: React.FC<{
  title: string
  value: string | { text: string; icon?: string }[]
  show?: boolean
}> = ({ title, value, show = true }) => {
  if (!show) return null
  return (
    <div className="practitioner-attribute">
      <h3>{title}</h3>
      {isArray(value) ? (
        value.map(item => <p key={item.text}>{item.text}</p>)
      ) : (
        <p>{value}</p>
      )}
    </div>
  )
}

export const PractitionerDetails: React.FC = () => {
  const history = useHistory()
  const { search } = useLocation()
  const [isModalOpen, toggleModal, setIsModalOpen] = useToggle(false)
  const { isMobileView } = useMediaContext()
  const isSliceTemplate = useSliceTemplate()

  const spinnerColor = isSliceTemplate
    ? palette.brand.orange
    : palette.brand.pink

  const {
    npi: identifierNpi,
    key: identifierKey,
    providerDistance,
  } = Object.fromEntries(new URLSearchParams(search).entries())

  const { practitionerDetails } = usePractitionerDetails({
    npi: identifierNpi,
    key: identifierKey,
  })

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (isModalOpen) {
      timer = setTimeout(() => {
        const a = document.createElement('a')
        a.setAttribute(
          'href',
          practitionerDetails?.practiceWebpageURL as string
        )
        a.setAttribute('target', '_blank')
        a.click()
        setIsModalOpen(false)
      }, 3000)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [isModalOpen, practitionerDetails?.practiceWebpageURL])

  if (!practitionerDetails)
    return <Spinner containerHeight="90vh" color={spinnerColor} size="25px" />

  const {
    firstName,
    lastName,
    title,
    primarySpecialty,
    practicePhone,
    practiceFax,
    practiceName,
    practiceWebpageURL,
    lat,
    long,
    virtualOption,
    address1,
    city,
    state,
    zipCode,
    address2,
    distance: practitionerDistance,
  } = practitionerDetails

  const isVirtualCare = checkIsVirtualCare(virtualOption)
  const isVirtualCareOnly = checkIsVirtualCareOnly(virtualOption)
  const hasPracticePhone = checkHasPracticePhone(practicePhone)
  const hasPracticeFax = checkHasPracticeFax(practiceFax)
  const hasPracticeContact = checkHasPracticeContact(practicePhone, practiceFax)

  const modalStyle = getModalStyle(isMobileView)

  const onRedirectUser = () => setIsModalOpen(true)

  const onRequestClose = () => setIsModalOpen(false)

  const about = aboutData(practitionerDetails)

  if (isSliceTemplate) {
    return (
      <PractitionerDetailsSlice
        formattedName={formatNameWithTitle(firstName, lastName, title)}
        primarySpecialty={primarySpecialty}
        practiceName={practiceName}
        lat={lat}
        long={long}
        practiceWebpageURL={sanitizeUrl(practiceWebpageURL)}
        goBack={history.goBack}
        isMobileView={isMobileView}
        practitionerDetails={practitionerDetails}
      />
    )
  }

  return (
    <StyledPractitionerDetails>
      <ContentSection>
        <Column>
          <button
            className="back-button"
            type="button"
            onClick={history.goBack}
          >
            <Icon icon="arrow-left" color={palette.ui.blue} size="1.5rem" />
            <h3>Back</h3>
          </button>

          <div className="title">
            <h1>
              {formatNameWithTitle(firstName, lastName, title)}
              <span>
                {isVirtualCare && (
                  <Icon
                    icon={isSliceTemplate ? 'slice-video' : 'video'}
                    color={palette.brand.teal}
                    size="1.5rem"
                  />
                )}
              </span>
            </h1>
          </div>
          <h3 className="">{primarySpecialty}</h3>

          <h2>About</h2>

          {about.map(item => {
            return (
              <PractitionerAttribute
                key={item.title}
                show={item.show ?? true}
                title={item.title}
                value={item.value}
              />
            )
          })}
        </Column>

        <Column>
          {!isVirtualCareOnly ? (
            <div className="map">
              <Map
                pins={[
                  {
                    id: 'practitioner',
                    latitude: lat,
                    longitude: long,
                  },
                ]}
                style={{
                  width: 530,
                  height: 400,
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              />
            </div>
          ) : (
            <div className="spacer" />
          )}

          <h2>Locations</h2>
          <div className="location">
            <div>
              <div>
                <div>
                  <Icon
                    icon="map-pin"
                    color={palette.brand.teal}
                    size="1.5rem"
                  />
                  <p>{practiceName}</p>
                </div>
                {!isVirtualCareOnly && (
                  <>
                    <span>
                      {address1} {address2 ? `, ${address2}` : ''}
                    </span>
                    <span>
                      {city}, {state}, {zipCode}
                    </span>
                  </>
                )}
              </div>
            </div>

            {!isVirtualCareOnly && (
              <p className="distance">
                {practitionerDistance ?? providerDistance ?? ''}{' '}
                {practitionerDistance || providerDistance ? ' mi' : ''}
              </p>
            )}
          </div>
          {isVirtualCare ? (
            <div className="wrapper">
              <p>
                <Icon
                  icon={isSliceTemplate ? 'slice-video' : 'video'}
                  color={palette.brand.teal}
                  size="1.5rem"
                />
                {isVirtualCareOnly ? (
                  <span>Virtual care only</span>
                ) : (
                  <span>Virtual care available</span>
                )}
              </p>
            </div>
          ) : null}
          {hasPracticeContact && (
            <div className="wrapper">
              {hasPracticePhone && (
                <p>
                  <Icon
                    icon={isSliceTemplate ? 'slice-phone' : 'phone'}
                    color={palette.brand.teal}
                    size="1.5rem"
                  />
                  <span>{practicePhone}</span>
                </p>
              )}
              {hasPracticeFax && (
                <p>
                  <Icon icon="fax" color={palette.brand.teal} size="1.5rem" />
                  <span>{practiceFax}</span>
                </p>
              )}
            </div>
          )}

          {practiceWebpageURL ? (
            <div className="website-link">
              <a onClick={onRedirectUser}>
                Learn more about this doctor
                <Icon icon="external-link" size="1.25rem" />
              </a>
            </div>
          ) : null}
          <Modal style={modalStyle} isOpen={isModalOpen}>
            <RedirectModal>
              <div>
                <h1 className="modal-title">See you later!</h1>
                <img src={centivoLogo} alt="centivo logo" />
              </div>
              <p>
                You are leaving Centivo and will be automatically redirected.
              </p>
              <Button
                buttonType="secondary"
                onClick={onRequestClose}
                style={{ width: '295px' }}
              >
                Cancel
              </Button>
            </RedirectModal>
          </Modal>
        </Column>
      </ContentSection>
    </StyledPractitionerDetails>
  )
}
